import { z } from "zod";
import { dateOnlySchema, idParamSchema, listQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { csvArraySchema, listResponseSchema, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * TEAM WORKLOAD & CAPACITY PLANNING (PRD §8 Item 12).
 *
 * Four rules shape every schema here.
 *
 *  1. **Capacity is a calendar, not a number.** A member's weekly minutes are
 *     spread over the workspace's working days, and a holiday, an alternate
 *     Saturday or an approved leave day removes that day's share. US-12.1 says
 *     "capacity minus holidays and approved leave" and this contract never
 *     lets a caller see one without the other.
 *  2. **Committed work is shown in its parts.** `taskMinutes` (estimates
 *     spread from start to due), `allocationMinutes` (a project membership's
 *     `allocationPercent`) and `bookingMinutes` (US-12.5 soft/hard bookings)
 *     are summed into `committedMinutes` AND reported separately, because a
 *     PM who cannot see why someone is red will not trust the colour.
 *  3. **Percentages are basis points.** 87.5% is 8750, never 0.875 — the same
 *     rule the metric dictionary uses, so a workload tile and a report tile
 *     are the same kind of number.
 *  4. **Money is masked, not hidden.** The forecast's rupee side is `null` for
 *     a caller without `financials:view`; the hours side is always readable,
 *     because "are we short 200 hours in November" is not a financial secret
 *     (PRD F3).
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * The three working-week patterns the setup wizard offers (PRD §5.1).
 * `mon_sat_alt` is the Indian one a weekday bitmap cannot express: six days a
 * week, except the 2nd and 4th Saturday of every month.
 */
export const WORKING_PATTERNS = ["mon_fri", "mon_sat", "mon_sat_alt"] as const;
export type WorkingPattern = (typeof WORKING_PATTERNS)[number];
export const workingPatternSchema = z.enum(WORKING_PATTERNS);

export const WORKING_PATTERN_LABELS: Record<WorkingPattern, string> = {
  mon_fri: "Monday to Friday",
  mon_sat: "Monday to Saturday",
  mon_sat_alt: "Monday to Saturday, 2nd & 4th Saturday off",
};

/**
 * `leave` is personal time off, `holiday` a company-wide shutdown entered per
 * member, `public_holiday` a gazetted one. They behave identically against
 * capacity; the type is what a manager reports on.
 */
export const LEAVE_TYPES = ["leave", "holiday", "public_holiday"] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];
export const leaveTypeSchema = z.enum(LEAVE_TYPES);

/** Only `approved` leave removes availability — a pending request warns, it does not book. */
export const LEAVE_STATUSES = ["pending", "approved", "rejected", "cancelled"] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const leaveStatusSchema = z.enum(LEAVE_STATUSES);

/** `hard` is committed resourcing; `soft` is a pencilled-in plan (US-12.5). */
export const BOOKING_KINDS = ["soft", "hard"] as const;
export type BookingKind = (typeof BOOKING_KINDS)[number];
export const bookingKindSchema = z.enum(BOOKING_KINDS);

/** Under 70% / healthy / over 100% — the bands US-12.3 names. */
export const WORKLOAD_BANDS = ["free", "healthy", "overbooked"] as const;
export type WorkloadBand = (typeof WORKLOAD_BANDS)[number];
export const workloadBandSchema = z.enum(WORKLOAD_BANDS);

export const WORKLOAD_GRANULARITIES = ["day", "week"] as const;
export type WorkloadGranularity = (typeof WORKLOAD_GRANULARITIES)[number];
export const workloadGranularitySchema = z.enum(WORKLOAD_GRANULARITIES);

/** Why a person appears in the at-risk queue (US-12.6). */
export const AT_RISK_REASONS = ["unassigned", "overbooked", "on_leave", "unscheduled"] as const;
export type AtRiskReason = (typeof AT_RISK_REASONS)[number];
export const atRiskReasonSchema = z.enum(AT_RISK_REASONS);

// ---------------------------------------------------------------------------
// Leave (US-12.2)
// ---------------------------------------------------------------------------

/** One absence. `minutesPerDay` null means the whole working day is off. */
export const leaveSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  type: leaveTypeSchema,
  status: leaveStatusSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Half days: 240 on a 480-minute day. Null = the whole day. */
  minutesPerDay: z.number().int().nullable(),
  reason: z.string().nullable(),
  /** Working days the absence actually consumes, holidays and weekends removed. */
  workingDays: z.number().int(),
  decidedByUserId: uuidSchema.nullable(),
  decidedBy: userRefSchema.nullable(),
  decidedAt: timestampSchema.nullable(),
  decisionNote: z.string().nullable(),
  ...timestampsShape,
});
export type LeaveDto = z.infer<typeof leaveSchema>;

const leaveWritableShape = {
  userId: uuidSchema,
  type: leaveTypeSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  minutesPerDay: z.number().int().min(1).max(1440).nullable(),
  reason: z.string().trim().max(500).nullable(),
};

/**
 * POST /api/v1/leave (auth; `time:manage` to file for someone else) → 201 Leave.
 * Overlapping an existing live request → CONFLICT; `endDate` before `startDate` → 422.
 */
export const createLeaveInputSchema = z
  .object({
    ...leaveWritableShape,
    userId: leaveWritableShape.userId.optional(),
    type: leaveWritableShape.type.optional(),
    minutesPerDay: leaveWritableShape.minutesPerDay.optional(),
    reason: leaveWritableShape.reason.optional(),
  })
  .strict();
export type CreateLeaveInput = z.input<typeof createLeaveInputSchema>;

/** PATCH /api/v1/leave/:id (own while pending, else `time:manage`) → Leave. */
export const updateLeaveInputSchema = z
  .object({
    type: leaveWritableShape.type,
    startDate: leaveWritableShape.startDate,
    endDate: leaveWritableShape.endDate,
    minutesPerDay: leaveWritableShape.minutesPerDay,
    reason: leaveWritableShape.reason,
  })
  .partial()
  .strict();
export type UpdateLeaveInput = z.input<typeof updateLeaveInputSchema>;

/** POST /api/v1/leave/:id/approve | /reject (perm `time:manage`) → Leave. */
export const reviewLeaveInputSchema = z.object({ note: z.string().trim().max(500).nullable().optional() }).strict();
export type ReviewLeaveInput = z.input<typeof reviewLeaveInputSchema>;

export const listLeaveQuerySchema = listQuerySchema
  .extend({
    userId: uuidSchema.optional(),
    status: leaveStatusSchema.optional(),
    type: leaveTypeSchema.optional(),
    /** Overlap, not containment: any absence touching [from, to]. */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    sort: z.enum(["startDate", "createdAt"]).optional(),
  })
  .strict();
export type ListLeaveQuery = z.infer<typeof listLeaveQuerySchema>;

// ---------------------------------------------------------------------------
// Bookings (US-12.5)
// ---------------------------------------------------------------------------

/** "Sneha, 20 h/week on Client X's retainer, Oct–Dec" — resourcing before tasks exist. */
export const bookingSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  name: z.string(),
  kind: bookingKindSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  minutesPerWeek: z.number().int(),
  notes: z.string().nullable(),
  ...timestampsShape,
});
export type BookingDto = z.infer<typeof bookingSchema>;

const bookingWritableShape = {
  userId: uuidSchema,
  clientId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  name: z.string().trim().min(1).max(200),
  kind: bookingKindSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  minutesPerWeek: z.number().int().min(0).max(10_080),
  notes: z.string().trim().max(1000).nullable(),
};

/** POST /api/v1/capacity/bookings (perm `projects:write`) → 201 Booking. */
export const createBookingInputSchema = z
  .object({
    ...bookingWritableShape,
    clientId: bookingWritableShape.clientId.optional(),
    projectId: bookingWritableShape.projectId.optional(),
    kind: bookingWritableShape.kind.optional(),
    notes: bookingWritableShape.notes.optional(),
  })
  .strict();
export type CreateBookingInput = z.input<typeof createBookingInputSchema>;

/** PATCH /api/v1/capacity/bookings/:id (perm `projects:write`) → Booking. */
export const updateBookingInputSchema = z.object(bookingWritableShape).partial().strict();
export type UpdateBookingInput = z.input<typeof updateBookingInputSchema>;

export const listBookingsQuerySchema = listQuerySchema
  .extend({
    userId: uuidSchema.optional(),
    projectId: uuidSchema.optional(),
    clientId: uuidSchema.optional(),
    kind: bookingKindSchema.optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    sort: z.enum(["startDate", "createdAt"]).optional(),
  })
  .strict();
export type ListBookingsQuery = z.infer<typeof listBookingsQuerySchema>;

// ---------------------------------------------------------------------------
// The workload grid (US-12.3)
// ---------------------------------------------------------------------------

/** One cell: a member on one day, or a member over one week. */
export const workloadCellSchema = z.object({
  /** The day, or the first day of the week for `granularity=week`. */
  date: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Capacity before holidays and leave — daily share × working days in the bucket. */
  capacityMinutes: z.number().int(),
  /** capacity − holidays − approved leave. Zero on a Sunday or Diwali. */
  availableMinutes: z.number().int(),
  leaveMinutes: z.number().int(),
  holidayDays: z.number().int(),
  workingDays: z.number().int(),
  taskMinutes: z.number().int(),
  allocationMinutes: z.number().int(),
  bookingMinutes: z.number().int(),
  committedMinutes: z.number().int(),
  /** Hours actually tracked in the bucket (time module facts) — the reality check. */
  trackedMinutes: z.number().int(),
  billableTrackedMinutes: z.number().int(),
  /** committed ÷ available in bps; null when nothing was available to fill. */
  utilisationBps: z.number().int().nullable(),
  /** billable tracked ÷ available in bps; null when nothing was available (US-10.8). */
  billableUtilisationBps: z.number().int().nullable(),
  band: workloadBandSchema,
  overbooked: z.boolean(),
  onLeave: z.boolean(),
});
export type WorkloadCellDto = z.infer<typeof workloadCellSchema>;

/** One member's row across the grid, plus that row's totals. */
export const workloadRowSchema = z.object({
  userId: uuidSchema,
  user: userRefSchema.nullable(),
  roleKey: z.string().nullable(),
  title: z.string().nullable(),
  weeklyCapacityMinutes: z.number().int(),
  pattern: workingPatternSchema,
  cells: z.array(workloadCellSchema),
  capacityMinutes: z.number().int(),
  availableMinutes: z.number().int(),
  committedMinutes: z.number().int(),
  trackedMinutes: z.number().int(),
  billableTrackedMinutes: z.number().int(),
  utilisationBps: z.number().int().nullable(),
  billableUtilisationBps: z.number().int().nullable(),
  /** How many buckets in this row are over 100% — the "how bad" number. */
  overbookedBuckets: z.number().int(),
  band: workloadBandSchema,
});
export type WorkloadRowDto = z.infer<typeof workloadRowSchema>;

/** GET /api/v1/workload (perm `projects:read`) → people × days or weeks. */
export const workloadGridSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  granularity: workloadGranularitySchema,
  pattern: workingPatternSchema,
  /** The dates in the window that are non-working for everyone, with their names. */
  holidays: z.array(z.object({ date: dateOnlySchema, name: z.string() })),
  buckets: z.array(z.object({ date: dateOnlySchema, endDate: dateOnlySchema, label: z.string() })),
  rows: z.array(workloadRowSchema),
  /** Totals across every row — the "is the agency full" number. */
  capacityMinutes: z.number().int(),
  availableMinutes: z.number().int(),
  committedMinutes: z.number().int(),
  utilisationBps: z.number().int().nullable(),
  /** Estimated, assigned work that has no dates and so lands in no bucket. */
  unscheduledMinutes: z.number().int(),
});
export type WorkloadGridDto = z.infer<typeof workloadGridSchema>;

export const workloadQuerySchema = z
  .object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    granularity: workloadGranularitySchema.optional(),
    /** A ROLE id today — the workspace's closest thing to a team until departments land. */
    teamId: uuidSchema.optional(),
    userIds: csvArraySchema(uuidSchema, 100).optional(),
    projectId: uuidSchema.optional(),
    clientId: uuidSchema.optional(),
    /** Include members with no committed work at all (default true). */
    includeIdle: queryBooleanSchema.optional(),
  })
  .strict();
export type WorkloadQuery = z.infer<typeof workloadQuerySchema>;

// ---------------------------------------------------------------------------
// Unassigned and at-risk work (US-12.4, US-12.6)
// ---------------------------------------------------------------------------

/** A piece of work that nobody owns, or that nobody can finish in time. */
export const workloadItemSchema = z.object({
  taskId: uuidSchema,
  number: z.string(),
  title: z.string(),
  url: z.string(),
  projectId: uuidSchema.nullable(),
  clientId: uuidSchema.nullable(),
  assigneeId: uuidSchema.nullable(),
  assignee: userRefSchema.nullable(),
  startDate: dateOnlySchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  estimateMinutes: z.number().int().nullable(),
  priority: z.string(),
  statusCategory: z.string(),
  version: z.number().int(),
  reasons: z.array(atRiskReasonSchema),
});
export type WorkloadItemDto = z.infer<typeof workloadItemSchema>;

/** GET /api/v1/workload/unassigned (perm `projects:read`) and /workload/at-risk. */
export const workloadItemsSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  items: z.array(workloadItemSchema),
  totalEstimateMinutes: z.number().int(),
});
export type WorkloadItemsDto = z.infer<typeof workloadItemsSchema>;

export const workloadItemsQuerySchema = z
  .object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    projectId: uuidSchema.optional(),
    clientId: uuidSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
  })
  .strict();
export type WorkloadItemsQuery = z.infer<typeof workloadItemsQuerySchema>;

/** POST /api/v1/workload/reassign (perm `tasks:write`) → the moved task and any warnings. */
export const reassignInputSchema = z
  .object({
    taskId: uuidSchema,
    /** Null unassigns — the "put it back in the pool" half of the drag. */
    assigneeId: uuidSchema.nullable(),
    /** Optimistic concurrency: a stale value answers 409 (US-12 acceptance). */
    version: z.number().int().min(1),
    startDate: dateOnlySchema.nullable().optional(),
    dueDate: dateOnlySchema.nullable().optional(),
    /** Refuse the move when it would overbook or land on leave, instead of warning. */
    strict: z.boolean().optional(),
  })
  .strict();
export type ReassignInput = z.input<typeof reassignInputSchema>;

export const capacityWarningSchema = z.object({
  code: z.enum(["overbooked", "on_leave", "no_capacity", "not_a_working_day", "past_due"]),
  message: z.string(),
  date: dateOnlySchema.nullable(),
  availableMinutes: z.number().int().nullable(),
  committedMinutes: z.number().int().nullable(),
});
export type CapacityWarningDto = z.infer<typeof capacityWarningSchema>;

export const reassignResultSchema = z.object({
  taskId: uuidSchema,
  assigneeId: uuidSchema.nullable(),
  assignee: userRefSchema.nullable(),
  version: z.number().int(),
  startDate: dateOnlySchema.nullable(),
  dueDate: dateOnlySchema.nullable(),
  warnings: z.array(capacityWarningSchema),
});
export type ReassignResultDto = z.infer<typeof reassignResultSchema>;

// ---------------------------------------------------------------------------
// Utilisation (US-10.8) — by member and by team
// ---------------------------------------------------------------------------

export const utilisationRowSchema = z.object({
  /** "member" or "team" — what `id` points at. */
  dimension: z.enum(["member", "team"]),
  id: uuidSchema.nullable(),
  label: z.string(),
  availableMinutes: z.number().int(),
  trackedMinutes: z.number().int(),
  billableMinutes: z.number().int(),
  /** tracked ÷ available, bps. */
  utilisationBps: z.number().int().nullable(),
  /** billable ÷ available, bps — the number US-10.8 sets a target against. */
  billableUtilisationBps: z.number().int().nullable(),
  /** billable ÷ tracked, bps: how much of what was logged is chargeable. */
  billableShareBps: z.number().int().nullable(),
  /** Billed value ÷ tracked hours; null without `financials:view` or with no hours. */
  effectiveHourlyMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
});
export type UtilisationRowDto = z.infer<typeof utilisationRowSchema>;

/** GET /api/v1/capacity/utilisation (perms `reports:view`). Money masked without `financials:view`. */
export const utilisationSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  rows: z.array(utilisationRowSchema),
  totals: utilisationRowSchema,
});
export type UtilisationDto = z.infer<typeof utilisationSchema>;

export const utilisationQuerySchema = z
  .object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    dimension: z.enum(["member", "team"]).optional(),
    teamId: uuidSchema.optional(),
    userIds: csvArraySchema(uuidSchema, 100).optional(),
  })
  .strict();
export type UtilisationQuery = z.infer<typeof utilisationQuerySchema>;

// ---------------------------------------------------------------------------
// The forecast (the hiring / bench signal)
// ---------------------------------------------------------------------------

/** One week (or month) of demand against supply. */
export const forecastBucketSchema = z.object({
  date: dateOnlySchema,
  endDate: dateOnlySchema,
  label: z.string(),
  availableMinutes: z.number().int(),
  /** Estimates on work already won and scheduled. */
  committedMinutes: z.number().int(),
  /** Weighted pipeline converted to delivery minutes at the effort rate. */
  expectedMinutes: z.number().int(),
  demandMinutes: z.number().int(),
  /** available − demand. Negative is a hiring signal, positive is bench. */
  surplusMinutes: z.number().int(),
  /** demand ÷ available, bps. */
  loadBps: z.number().int().nullable(),
  /** Weighted pipeline landing in this bucket; null without `financials:view`. */
  weightedPipelineMinor: minorSchema.nullable(),
});
export type ForecastBucketDto = z.infer<typeof forecastBucketSchema>;

/** GET /api/v1/capacity/forecast (perms `reports:view` + `leads:read`). */
export const forecastSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  /** How the rupee-to-hours conversion was obtained, so the number can be argued with. */
  basis: z.enum(["service_rate", "workspace_rate", "realised_rate", "none"]),
  /** Delivered revenue per hour used for the conversion; null when masked or unknown. */
  revenuePerHourMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  buckets: z.array(forecastBucketSchema),
  availableMinutes: z.number().int(),
  demandMinutes: z.number().int(),
  surplusMinutes: z.number().int(),
  loadBps: z.number().int().nullable(),
  /** Full-time-equivalent people the gap represents, ×100 (250 = 2.5 people). */
  fteGapCentis: z.number().int(),
  /** "hire" when short for two buckets running, "bench" when under 70%, else "balanced". */
  signal: z.enum(["hire", "balanced", "bench"]),
  weightedPipelineMinor: minorSchema.nullable(),
  leadsCounted: z.number().int(),
  /** Open leads with no expected close date — demand the forecast cannot place. */
  leadsUnscheduled: z.number().int(),
});
export type ForecastDto = z.infer<typeof forecastSchema>;

export const forecastQuerySchema = z
  .object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    /** Price the whole pipeline at one service's effort rate. */
    serviceId: uuidSchema.optional(),
    pipelineId: uuidSchema.optional(),
    teamId: uuidSchema.optional(),
    userIds: csvArraySchema(uuidSchema, 100).optional(),
  })
  .strict();
export type ForecastQuery = z.infer<typeof forecastQuerySchema>;

// ---------------------------------------------------------------------------
// Settings and effort rates
// ---------------------------------------------------------------------------

/** GET /api/v1/capacity/settings (auth) → the workspace's capacity rules. */
export const capacitySettingsSchema = z.object({
  /** Null = derive from `organizations.working_hours.days`. */
  pattern: workingPatternSchema.nullable(),
  /** The pattern actually in force once the derivation has run. */
  effectivePattern: workingPatternSchema,
  /** Utilisation at or above this is "overbooked" (default 10000 = 100%). */
  overbookedBps: z.number().int(),
  /** Below this is "free" (default 7000 = 70%, the band US-12.3 names). */
  healthyFromBps: z.number().int(),
  forecastHorizonWeeks: z.number().int(),
  /** Workspace fallback for the pipeline-to-hours conversion; null = use the realised rate. */
  revenuePerHourMinor: minorSchema.nullable(),
  currency: currencyCodeSchema,
  ...timestampsShape,
});
export type CapacitySettingsDto = z.infer<typeof capacitySettingsSchema>;

/** PATCH /api/v1/capacity/settings (perm `org:manage`) → CapacitySettings. */
export const updateCapacitySettingsInputSchema = z
  .object({
    pattern: workingPatternSchema.nullable(),
    overbookedBps: z.number().int().min(1000).max(50_000),
    healthyFromBps: z.number().int().min(0).max(50_000),
    forecastHorizonWeeks: z.number().int().min(1).max(52),
    revenuePerHourMinor: minorSchema.min(0).nullable(),
  })
  .partial()
  .strict();
export type UpdateCapacitySettingsInput = z.input<typeof updateCapacitySettingsInputSchema>;

/** How much delivered revenue one hour of a service produces — the forecast's conversion. */
export const effortRateSchema = z.object({
  id: uuidSchema,
  /** Null is the workspace default row. */
  serviceId: uuidSchema.nullable(),
  serviceName: z.string().nullable(),
  revenuePerHourMinor: minorSchema,
  currency: currencyCodeSchema,
  ...timestampsShape,
});
export type EffortRateDto = z.infer<typeof effortRateSchema>;

/** PUT /api/v1/capacity/effort-rates (perm `org:manage`) → the upserted rate. */
export const upsertEffortRateInputSchema = z
  .object({
    serviceId: uuidSchema.nullable().optional(),
    revenuePerHourMinor: minorSchema.min(1),
    currency: currencyCodeSchema.optional(),
  })
  .strict();
export type UpsertEffortRateInput = z.input<typeof upsertEffortRateInputSchema>;

export const effortRatesSchema = listResponseSchema(effortRateSchema);
export type EffortRatesDto = z.infer<typeof effortRatesSchema>;

export const leaveParamSchema = idParamSchema;
